import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** @type {HTMLButtonElement | undefined} */
  loadMoreButton;
  /** @type {HTMLButtonElement | undefined} */
  countEl;

  /* SWITCH(ES) TODO: expose as admin settings */
  
  /** @type {boolean | null} 
   * NOTE (DC): 
   * this is a slight misnomer. what this does is disable the infinite scroll from automatically paginating, and instead provides a 'Load More' button to manually advance pagination.
   * so technically, infinite scroll would still be enabled, just the pagination changes from auto to manual
   * (TODO: consider renaming in the future)
   */
  disableInfiniteScroll = true;

  /** @type {boolean | null} 
   * NOTE (DC):
   * this disables infinite scroll entirely (regardless of auto or manual pagination), in favor of true pagination.
   * so a 'true' value would override any value set on disableInfiniteScroll property above.
  */
  enableTruePagination = true;
  pagerTypeOptions = [
    'dropdown',
    'list'
  ];
  selectedPagerType = this.pagerTypeOptions[1];


  /* /switch(es) */



  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();

    if (this.enableTruePagination) {
      this.#setupTruePagination();
    } else if (this.disableInfiniteScroll) {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#setupLoadMoreButton(); // infinite scroll -- manual pagination
    } else {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore(); // infinite scroll -- auto pagination
    }
    

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }



  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }
    

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }


  /************* DC ADDITIONS *************/

  // NEW (DC // 8/6/26): trigger infinite scroll to continue via manual action (button click)
  #setupLoadMoreButton() {
    this.loadMoreButton?.remove();
    this.loadMoreButton = undefined;
    this.countEl?.remove();
    this.countEl = undefined;

    const { viewMoreNext, grid } = this.refs;
    if (!viewMoreNext || !grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    const { container, button } = this.#generateLoadMoreHTML();
    this.#updateCount();

    button.addEventListener('click', async () => {
      button.disabled = true;
      button.textContent = 'Loading…';

      await this.#renderNextPage();
      this.#updateCount();

      const next = this.#getPage('next');
      if (!next || !this.#shouldUsePage(next)) {
        button.remove();
        this.loadMoreButton = undefined;
      } else {
        button.disabled = false;
        button.textContent = 'Load More';
      }
    });

    this.loadMoreButton = button;
    viewMoreNext.insertAdjacentElement('afterend', container);
  }

  #updateCount() {
    if (!this.countEl) return;
    const total = Number(this.refs.grid?.dataset.totalCount);
    const current = this.refs.cards?.length ?? 0;
    if (!total || !current) return;
    this.countEl.textContent = `Viewing ${current} of ${total} items`;
  }

  #generateLoadMoreHTML() {
    const container = document.createElement('div');
    container.classList.add('container__load-more', 'mt-12');

    const button = document.createElement('button');
    button.textContent = 'Load More';
    button.classList.add('paginated-list__load-more', 'btn', 'ts-btn');
    button.setAttribute('type', 'button');
    button.style.display = 'block';

    this.countEl = document.createElement('p');
    this.countEl.classList.add('paginated-list__count');
    this.countEl.style.width = 'fit-content';
    this.countEl.style.margin = '0 auto';
    this.countEl.style.marginTop = '1em';

    container.append(button, this.countEl);
    return { button, container };
  }


  // NEW (DC // 8/18/26): adding support for true pagination

  #setupTruePagination() {
    const { viewMoreNext, grid } = this.refs;
    if (!viewMoreNext || !grid) return;

    const currentPage = this.#getCurrentPage();
    const lastPage = Number(grid.dataset.lastPage);

    if (lastPage <= 1) return;

    const nav = document.createElement('nav');
    nav.classList.add('paginated-list__pagination');

    // // Previous
    // const prev = document.createElement('a');
    // prev.textContent = 'Previous';
    // prev.classList.add('paginated-list__page-link');
    // if (currentPage > 1) {
    //   prev.href = this.#getPageUrl(currentPage - 1);
    // } else {
    //   prev.setAttribute('aria-disabled', 'true');
    // }

    if (this.selectedPagerType === 'list') {
      // Numbered page links
      const pageList = document.createElement('ol');
      pageList.classList.add('paginated-list__page-numbers');
      nav.classList.add('list');
      /* list all pages */
      // for (let i = 1; i <= lastPage; i++) {
      //   const li = document.createElement('li');
      //   const a = document.createElement('a');
      //   a.href = this.#getPageUrl(i);
      //   a.textContent = String(i);
      //   a.classList.add('paginated-list__page-number');
      //   if (i === currentPage) {
      //     a.classList.add('is-current');
      //     a.setAttribute('aria-current', 'page');
      //   }
      //   li.append(a);
      //   pageList.append(li);
      // }
      /* truncate page list */
      for (const pageNum of this.#getPageNumbers(currentPage, lastPage)) {
        const li = document.createElement('li');
        if (pageNum === null) {
          // Ellipsis
          const ellipsis = document.createElement('span');
          ellipsis.textContent = '…';
          ellipsis.classList.add('paginated-list__page-ellipsis');
          li.append(ellipsis);
        } else {
          const a = document.createElement('a');
          a.href = this.#getPageUrl(pageNum);
          a.textContent = String(pageNum);
          a.classList.add('paginated-list__page-number');
          if (pageNum === currentPage) {
            li.classList.add('is-current');
            a.classList.add('is-current');
            a.setAttribute('aria-current', 'page');
          }
          li.append(a);
        }
        pageList.append(li);
      }

      // Previous
      const prev = document.createElement('a');
      prev.textContent = 'Previous';
      prev.classList.add('paginated-list__page-link', 'hide-mobile', 'prev');
      if (currentPage > 1) {
        prev.href = this.#getPageUrl(currentPage - 1);
      } else {
        prev.setAttribute('aria-disabled', 'true');
      }

      // Next
      const next = document.createElement('a');
      next.textContent = 'Next';
      next.classList.add('paginated-list__page-link', 'hide-mobile', 'next');
      if (currentPage < lastPage) {
        next.href = this.#getPageUrl(currentPage + 1);
      } else {
        next.setAttribute('aria-disabled', 'true');
      }

      nav.append(prev, pageList, next);
      viewMoreNext.insertAdjacentElement('afterend', nav);

    } else if (this.selectedPagerType === 'dropdown') {
      // Page selector dropdown
      const select = document.createElement('select');
      select.classList.add('paginated-list__page-select');
      nav.classList.add('dropdown');
      for (let i = 1; i <= lastPage; i++) {
        const option = document.createElement('option');
        option.value = this.#getPageUrl(i);
        option.textContent = String(i);
        if (i === currentPage) option.selected = true;
        select.append(option);
      }
      select.addEventListener('change', () => {
        window.location.href = select.value;
      });

      // Separator
      const sep = document.createElement('span');
      sep.textContent = '/';
      sep.classList.add('paginated-list__page-sep');

      // Last page
      const total = document.createElement('span');
      total.textContent = String(lastPage);
      total.classList.add('paginated-list__page-total');

      // Previous
      const prev = document.createElement('a');
      prev.textContent = '<';
      prev.classList.add('paginated-list__page-link');
      if (currentPage > 1) {
        prev.href = this.#getPageUrl(currentPage - 1);
      } else {
        prev.setAttribute('aria-disabled', 'true');
      }

      // Next
      const next = document.createElement('a');
      next.textContent = '>';
      next.classList.add('paginated-list__page-link');
      if (currentPage < lastPage) {
        next.href = this.#getPageUrl(currentPage + 1);
      } else {
        next.setAttribute('aria-disabled', 'true');
      }

      nav.append(prev, select, sep, total, next);
      viewMoreNext.insertAdjacentElement('afterend', nav);
    }
  }

  #getPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return url.toString();
  }

  #getCurrentPage() {
    return Number(new URL(window.location.href).searchParams.get('page') || '1');
  }

  /**
   * Returns the page numbers to display, with null representing ellipsis gaps
   * @param {number} currentPage
   * @param {number} lastPage
   * @returns {(number | null)[]}
   */
  #getPageNumbers(currentPage, lastPage) {
    if (lastPage <= 7) {
      // No truncation needed — show all pages
      return Array.from({ length: lastPage }, (_, i) => i + 1);
    }

    const pages = new Set([1, lastPage]);

    // Always show 2 pages on either side of current
    for (let i = Math.max(2, currentPage - 2); i <= Math.min(lastPage - 1, currentPage + 2); i++) {
      pages.add(i);
    }

    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];

    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
        result.push(null); // gap → ellipsis
      }
      result.push(sorted[i]);
    }

    return result;
  }



  /************* / DC additions *************/


  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    // Wait for the DOM to update
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver((mutations) => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        if (this.enableTruePagination) {
          this.#setupTruePagination();
        } else if (this.disableInfiniteScroll) {
          this.#setupLoadMoreButton();
        } else {
          this.#observeViewMore();
        }

        

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}